import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { listEmailLog, type AdminEmailRow } from "@/lib/admin-emails.functions";

export const Route = createFileRoute("/_authenticated/admin/emails")({
  head: () => ({
    meta: [
      { title: "E-postlogg — Fenny admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminEmails,
});

type StatusFilter = "all" | "sent" | "failed" | "other";
type RangeFilter = "24h" | "7d" | "30d" | "all";
type SortKey = "sent_at" | "recipient_email" | "subject" | "kind" | "status";

const PAGE_SIZE = 50;

function fmt(d: string) {
  return new Date(d).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
}

function statusBadge(s: string) {
  if (s === "sent") return "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30";
  if (s === "failed") return "bg-rose-500/15 text-rose-300 border border-rose-500/30";
  return "bg-amber-500/15 text-amber-300 border border-amber-500/30";
}

function toCsv(rows: AdminEmailRow[]) {
  const headers = ["sent_at", "status", "recipient_email", "subject", "kind", "lead_email", "lead_source", "error"];
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => esc((r as any)[h])).join(",")),
  ].join("\n");
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function AdminEmails() {
  const fn = useServerFn(listEmailLog);
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["admin", "emails"],
    queryFn: () => fn(),
    refetchInterval: 30_000,
  });

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [range, setRange] = useState<RangeFilter>("7d");
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("sent_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<AdminEmailRow | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const stats = data?.stats;
  const allRows = data?.rows ?? [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const cutoff =
      range === "24h" ? Date.now() - 24 * 3600_000 :
      range === "7d" ? Date.now() - 7 * 24 * 3600_000 :
      range === "30d" ? Date.now() - 30 * 24 * 3600_000 : 0;

    const out = allRows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter && !(statusFilter === "other" && r.status !== "sent" && r.status !== "failed")) return false;
      if (kindFilter !== "all" && r.kind !== kindFilter) return false;
      if (cutoff && new Date(r.sent_at).getTime() < cutoff) return false;
      if (!needle) return true;
      return (
        r.recipient_email.toLowerCase().includes(needle) ||
        r.subject.toLowerCase().includes(needle) ||
        r.kind.toLowerCase().includes(needle) ||
        (r.lead_email?.toLowerCase().includes(needle) ?? false)
      );
    });

    out.sort((a, b) => {
      const av = (a[sortKey] ?? "") as string;
      const bv = (b[sortKey] ?? "") as string;
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [allRows, q, statusFilter, kindFilter, range, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  }

  function copy(text: string, key: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1200);
    });
  }

  const successRate = stats && stats.total > 0 ? Math.round((stats.sent / stats.total) * 100) : 0;
  const maxDaily = Math.max(1, ...(stats?.daily.map((d) => d.sent + d.failed) ?? [1]));

  return (
    <div className="p-6 md:p-8 space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">E-postlogg</h1>
          <p className="text-sm text-white/50">
            {stats ? `${stats.total} utskick · ${stats.uniqueRecipients} unika mottagare · ${successRate}% lyckade` : "Laddar…"}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="rounded-lg border border-white/10 px-3 py-2 text-sm text-white/70 hover:text-white hover:border-white/30"
          >
            {isFetching ? "Uppdaterar…" : "Uppdatera"}
          </button>
          <button
            onClick={() => downloadCsv(`emails-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(filtered))}
            className="rounded-lg border border-white/10 px-3 py-2 text-sm text-white/70 hover:text-white hover:border-white/30"
          >
            Exportera CSV
          </button>
        </div>
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Totalt", value: stats?.total ?? "—" },
          { label: "Skickade", value: stats?.sent ?? "—", tone: "text-emerald-300" },
          { label: "Misslyckade", value: stats?.failed ?? "—", tone: "text-rose-300" },
          { label: "24h", value: stats?.last24h ?? "—" },
          { label: "7 dagar", value: stats?.last7d ?? "—" },
        ].map((c) => (
          <div key={c.label} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="text-xs uppercase tracking-wider text-white/50">{c.label}</div>
            <div className={`mt-1 text-2xl font-semibold ${c.tone ?? "text-white"}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Daily chart */}
      {stats && stats.daily.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-white/70">Utskick per dag (senaste 14)</div>
            <div className="flex gap-3 text-xs text-white/50">
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-400" />Skickade</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-rose-400" />Misslyckade</span>
            </div>
          </div>
          <div className="flex items-end gap-1 h-28">
            {stats.daily.map((d) => {
              const total = d.sent + d.failed;
              const h = (total / maxDaily) * 100;
              const sentH = total > 0 ? (d.sent / total) * h : 0;
              const failH = total > 0 ? (d.failed / total) * h : 0;
              return (
                <div key={d.day} className="flex-1 flex flex-col justify-end items-stretch group relative">
                  <div className="bg-rose-400/80" style={{ height: `${failH}%` }} />
                  <div className="bg-emerald-400/80" style={{ height: `${sentH}%` }} />
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition text-[10px] bg-black/80 border border-white/10 rounded px-1.5 py-0.5 whitespace-nowrap">
                    {d.day}: {d.sent} ✓ / {d.failed} ✗
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <input
          placeholder="Sök mottagare, ämne, lead…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }}
          className="flex-1 min-w-[220px] rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-white/30"
        />
        <select
          value={kindFilter}
          onChange={(e) => { setKindFilter(e.target.value); setPage(0); }}
          className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-white/30"
        >
          <option value="all">Alla typer</option>
          {stats?.kinds.map((k) => (
            <option key={k.kind} value={k.kind}>{k.kind} ({k.count})</option>
          ))}
        </select>
        <div className="flex rounded-lg border border-white/10 overflow-hidden">
          {(["24h", "7d", "30d", "all"] as const).map((r) => (
            <button
              key={r}
              onClick={() => { setRange(r); setPage(0); }}
              className={`px-3 py-2 text-sm ${range === r ? "bg-white/10 text-white" : "text-white/60 hover:text-white"}`}
            >
              {r === "all" ? "Alla" : r}
            </button>
          ))}
        </div>
        <div className="flex rounded-lg border border-white/10 overflow-hidden">
          {(["all", "sent", "failed", "other"] as const).map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(0); }}
              className={`px-3 py-2 text-sm ${statusFilter === s ? "bg-white/10 text-white" : "text-white/60 hover:text-white"}`}
            >
              {s === "all" ? "Alla" : s === "sent" ? "Skickade" : s === "failed" ? "Misslyckade" : "Övriga"}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/50">
            <tr>
              {([
                ["sent_at", "Tid"],
                ["status", "Status"],
                ["recipient_email", "Mottagare"],
                ["subject", "Ämne"],
                ["kind", "Typ"],
              ] as [SortKey, string][]).map(([k, label]) => (
                <th
                  key={k}
                  onClick={() => toggleSort(k)}
                  className="text-left px-4 py-3 cursor-pointer select-none hover:text-white"
                >
                  {label}{sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {isLoading && (
              <tr><td colSpan={5} className="px-4 py-6 text-white/50">Laddar…</td></tr>
            )}
            {!isLoading && pageRows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-white/50">Inga utskick matchar filtret.</td></tr>
            )}
            {pageRows.map((r) => (
              <tr key={r.id} className="hover:bg-white/[0.03] cursor-pointer" onClick={() => setOpen(r)}>
                <td className="px-4 py-2 text-white/60 whitespace-nowrap">{fmt(r.sent_at)}</td>
                <td className="px-4 py-2">
                  <span className={`text-xs rounded-full px-2 py-0.5 ${statusBadge(r.status)}`}>{r.status}</span>
                </td>
                <td className="px-4 py-2 text-white truncate max-w-[240px]">{r.recipient_email}</td>
                <td className="px-4 py-2 text-white/80 truncate max-w-[360px]">{r.subject}</td>
                <td className="px-4 py-2 text-white/60">{r.kind}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-white/60">
        <div>
          {filtered.length === 0 ? "0" : `${page * PAGE_SIZE + 1}–${Math.min(filtered.length, (page + 1) * PAGE_SIZE)}`} av {filtered.length}
        </div>
        <div className="flex gap-2">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="rounded border border-white/10 px-3 py-1 disabled:opacity-30 hover:border-white/30"
          >
            ← Föregående
          </button>
          <span className="px-2 py-1">Sida {page + 1} / {pageCount}</span>
          <button
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className="rounded border border-white/10 px-3 py-1 disabled:opacity-30 hover:border-white/30"
          >
            Nästa →
          </button>
        </div>
      </div>

      {/* Top recipients */}
      {stats && stats.topRecipients.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-sm text-white/70 mb-3">Mest aktiva mottagare</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {stats.topRecipients.map((r) => (
              <div key={r.email} className="flex justify-between border-b border-white/5 py-1">
                <button
                  className="text-white/80 hover:text-white truncate text-left"
                  onClick={() => { setQ(r.email); setPage(0); }}
                >
                  {r.email}
                </button>
                <span className="text-white/50">{r.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setOpen(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className="relative ml-auto h-full w-full max-w-xl bg-[#0a0a0a] border-l border-white/10 p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setOpen(null)} className="text-white/60 hover:text-white text-sm">← Stäng</button>
              <span className={`text-xs rounded-full px-2 py-0.5 ${statusBadge(open.status)}`}>{open.status}</span>
            </div>
            <h2 className="text-lg font-semibold">{open.subject}</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <Row label="Till">
                <div className="flex items-center gap-2">
                  <a href={`mailto:${open.recipient_email}`} className="text-white hover:underline">{open.recipient_email}</a>
                  <button onClick={() => copy(open.recipient_email, "to")} className="text-xs text-white/40 hover:text-white">
                    {copied === "to" ? "kopierat" : "kopiera"}
                  </button>
                </div>
              </Row>
              <Row label="Typ"><span className="text-white/80">{open.kind}</span></Row>
              <Row label="Tid"><span className="text-white/80">{new Date(open.sent_at).toLocaleString("sv-SE")}</span></Row>
              {open.lead_email && (
                <Row label="Lead">
                  <span className="text-white/80">{open.lead_email}{open.lead_source ? ` (${open.lead_source})` : ""}</span>
                </Row>
              )}
              {open.error && (
                <Row label="Felmeddelande">
                  <pre className="text-rose-300 whitespace-pre-wrap text-xs bg-rose-500/10 border border-rose-500/20 p-3 rounded">{open.error}</pre>
                </Row>
              )}
              {open.provider_response && (
                <Row label="Provider-svar">
                  <div className="relative">
                    <button
                      onClick={() => copy(open.provider_response!, "prov")}
                      className="absolute right-2 top-2 text-[10px] text-white/40 hover:text-white"
                    >
                      {copied === "prov" ? "kopierat" : "kopiera"}
                    </button>
                    <pre className="text-xs bg-white/5 border border-white/10 p-3 rounded overflow-x-auto">{
                      (() => {
                        try { return JSON.stringify(JSON.parse(open.provider_response!), null, 2); }
                        catch { return open.provider_response; }
                      })()
                    }</pre>
                  </div>
                </Row>
              )}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-white/40 mb-1">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
